/**
 * @file React Query hooks for agent-proposed wheel authoring (C5).
 *
 * Confirm/undo go to LABIQ's WheelActionController under the author's own token — never to
 * healthcare's XoeActionController, which owns clinical writes. The batch identity is derived
 * client-side (deriveWheelBatchId) so a double-click or retry replays the same batch_id and
 * lands on the server's already_confirmed short-circuit instead of a duplicate batch.
 */

import { useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import {
	ConfirmWheelActionsPayload,
	ConfirmWheelActionsResult,
	UndoWheelActionsResult,
	WheelActionBatchStatusResult,
	WheelActionFailingItem,
	confirmWheelActions,
	fetchWheelActionBatch,
	undoWheelActions,
} from "../../api/geneCodes/WheelActionsApi";
import { fetchVariantCatalogue } from "../../api/geneCodes/VariantCatalogueApi";
import { deriveWheelBatchId, describeWheelBlastRadius, isWheelActionType } from "../../utils/geneCodes/wheelActions";
import { useWheelActionTarget } from "../../components/xoe/actions/WheelActionTargetContext";
import type { CatalogueVariant } from "../../types/geneCodes/VariantCatalogueTypes";
import type { WheelActionItemResult, WheelUndoItemResult } from "../../types/geneCodes/WheelActionTypes";
import type { XoeProposedAction } from "../../types/xoe/XoeTypes";

// ── Mutations ─────────────────────────────────────────────────────────────────

export type ConfirmWheelActionsVars = ConfirmWheelActionsPayload & { conversationUuid: string };

/** Confirm a wheel batch against labiq; invalidates the conversation so the thread refetches. */
export const useConfirmWheelActions = () => {
	const queryClient = useQueryClient();

	return useMutation<ConfirmWheelActionsResult, Error, ConfirmWheelActionsVars>({
		mutationFn: ({ conversationUuid: _conversationUuid, ...payload }) => confirmWheelActions(payload),
		onSuccess: (_data, vars) => {
			queryClient.invalidateQueries({ queryKey: ["xoeConversation", vars.conversationUuid] });
			queryClient.invalidateQueries({ queryKey: ["xoeConversations"] });
		},
	});
};

export const useUndoWheelActions = () => {
	return useMutation<UndoWheelActionsResult, Error, { batchId: string }>({
		mutationFn: ({ batchId }) => undoWheelActions(batchId),
	});
};

// ── Evidence ──────────────────────────────────────────────────────────────────

/**
 * Catalogue evidence for the SNPs a proposal wants to add, keyed by the proposal's own rs —
 * sent VERBATIM (the server matches SNPID/ORI_SNPID; case-folding here silently drops matches).
 * A failed lookup maps to null: no evidence is ever fabricated for a missing row.
 */
export const useWheelSnpEvidence = (rsList: string[], panelId: number | null) => {
	return useQuery<Record<string, CatalogueVariant | null>>({
		queryKey: ["wheelActionEvidence", panelId, rsList],
		enabled: panelId !== null && rsList.length > 0,
		queryFn: async () => {
			const pages = await Promise.all(rsList.map((rs) => fetchVariantCatalogue({ rs, panel_id: panelId ?? undefined, per_page: 5 }).catch(() => null)));
			const byRs: Record<string, CatalogueVariant | null> = {};
			rsList.forEach((rs, index) => {
				const page = pages[index];
				byRs[rs] = page ? page.variants.find((variant) => variant.rs === rs) ?? page.variants[0] ?? null : null;
			});
			return byRs;
		},
	});
};

// ── Batch orchestration ───────────────────────────────────────────────────────

export type WheelBatchPhase = "idle" | "pending" | "confirmed" | "failed" | "undone";

interface WheelBatchRecord {
	batchId: string;
	actionIds: string[];
	phase: Exclude<WheelBatchPhase, "idle">;
	result?: ConfirmWheelActionsResult;
	alreadyConfirmed?: boolean;
	error?: string | null;
	failingItem?: WheelActionFailingItem | null;
	undoResult?: UndoWheelActionsResult;
	alreadyUndone?: boolean;
	undoPending?: boolean;
	undoBlocked?: string | null;
	undoError?: string | null;
}

/** What one wheel card renders — derived per action from its batch record. */
export interface WheelActionCardState {
	canConfirm: boolean;
	panelId: number | null;
	phase: WheelBatchPhase;
	itemResult: WheelActionItemResult | null;
	alreadyConfirmed: boolean;
	forkedFromPanelId: number | null;
	error: string | null;
	failingItem: (WheelActionFailingItem & { mine: boolean }) | null;
	undoEntry: WheelUndoItemResult | null;
	alreadyUndone: boolean;
	undoPending: boolean;
	undoBlocked: string | null;
	undoError: string | null;
	onConfirm: () => void;
	onUndo: () => void;
}

export interface WheelAddAllState {
	count: number;
	radius: string;
	pending: boolean;
	onConfirm: () => void;
}

const failingItemFrom = (error: unknown): WheelActionFailingItem | null => {
	if (!axios.isAxiosError(error)) return null;
	const failing = (error.response?.data as { failing_item?: WheelActionFailingItem } | undefined)?.failing_item;
	return failing && typeof failing.index === "number" && typeof failing.action_type === "string" ? failing : null;
};

// labiq's messages here are static validation strings — never patient or clinical text.
const confirmErrorFrom = (error: unknown): string => {
	if (axios.isAxiosError(error)) {
		const message = (error.response?.data as { message?: unknown } | undefined)?.message;
		if (typeof message === "string" && message !== "") return message;
	}
	return "Could not apply this change. Please try again.";
};

/** A durable batch row folded into the SAME record shape the optimistic path writes, so the card
 * needs no rehydrated branch. A `pending` row is a confirm that never completed — it stays open. */
const rehydratedRecord = (batchId: string, actionIds: string[], status: WheelActionBatchStatusResult | undefined): WheelBatchRecord | null => {
	if (!status) return null;

	if (status.status === "confirmed" && status.result) {
		return { batchId, actionIds, phase: "confirmed", result: status.result, alreadyConfirmed: true };
	}

	if (status.status === "undone") {
		return {
			batchId,
			actionIds,
			phase: "undone",
			result: status.result ?? undefined,
			alreadyConfirmed: true,
			undoResult: status.undo_result ?? undefined,
			alreadyUndone: status.already_undone === true,
		};
	}

	return null;
};

export interface UseWheelActionBatchesArgs {
	/** The VISIBLE actions of one message, in message order — dismissed cards excluded by the caller. */
	actions: XoeProposedAction[];
	messageId: number;
	conversationUuid: string;
}

/**
 * Per-message wheel batch state: one confirm per card, "Add all" as ONE atomic labiq batch,
 * and undo on a confirmed batch. Lives in the card LIST so a batch of N cards shares one record.
 */
export const useWheelActionBatches = ({ actions, messageId, conversationUuid }: UseWheelActionBatchesArgs) => {
	const target = useWheelActionTarget();
	const confirmMutation = useConfirmWheelActions();
	const undoMutation = useUndoWheelActions();

	const [batches, setBatches] = useState<Record<string, WheelBatchRecord>>({});
	const [attemptByAction, setAttemptByAction] = useState<Record<string, string>>({});
	// Synchronous double-click guard — state updates re-render too late for the second click.
	const inFlightRef = useRef<Set<string>>(new Set());

	const wheelActions = useMemo(() => actions.filter((action) => isWheelActionType(action.action_type)), [actions]);

	// The groups a confirm could have been sent as: the whole message ("Add all") and each card alone.
	const candidates = useMemo(() => {
		const ids = wheelActions.map((action) => action.id);
		const groups = ids.length > 1 ? [ids, ...ids.map((id) => [id])] : ids.map((id) => [id]);
		const unique: Array<{ batchId: string; actionIds: string[] }> = [];
		groups.forEach((actionIds) => {
			// The SAME helper the confirm path uses — a hand-written id would miss the row silently.
			const batchId = deriveWheelBatchId(messageId, actionIds);
			if (!unique.some((entry) => entry.batchId === batchId)) unique.push({ batchId, actionIds });
		});
		return unique;
	}, [wheelActions, messageId]);

	// A 404 AND a transport failure both leave the card OPEN — a wrongly confirmed card hides work that never happened.
	const statusQueries = useQueries({
		queries: candidates.map(({ batchId }) => ({
			queryKey: ["wheelActionBatch", batchId],
			queryFn: () => fetchWheelActionBatch(batchId),
			retry: false,
			staleTime: 30_000,
		})),
	});

	const rehydratedRecords: Record<string, WheelBatchRecord> = {};
	const rehydratedAttempts: Record<string, string> = {};
	candidates.forEach(({ batchId, actionIds }, index) => {
		const record = rehydratedRecord(batchId, actionIds, statusQueries[index]?.data);
		if (!record) return;
		rehydratedRecords[batchId] = record;
		actionIds.forEach((id) => {
			if (!rehydratedAttempts[id]) rehydratedAttempts[id] = batchId;
		});
	});

	// Local state wins — the optimistic path is untouched and the server read only fills the gap.
	const recordOf = (actionId: string): WheelBatchRecord | undefined => {
		const batchId = attemptByAction[actionId] ?? rehydratedAttempts[actionId];
		if (!batchId) return undefined;
		return batches[batchId] ?? rehydratedRecords[batchId];
	};

	// Idle or failed = still open; pending/confirmed/undone may not enter another batch.
	const isOpen = (action: XoeProposedAction): boolean => {
		const record = recordOf(action.id);
		return !record || record.phase === "failed";
	};

	const confirmBatch = (batchActions: XoeProposedAction[]) => {
		if (!target) return;
		// Message order preserved — the server applies items in order (create_wheel retargets the rest).
		const ordered = wheelActions.filter((action) => batchActions.some((candidate) => candidate.id === action.id) && isOpen(action));
		if (ordered.length === 0) return;

		const actionIds = ordered.map((action) => action.id);
		const batchId = deriveWheelBatchId(messageId, actionIds);
		if (inFlightRef.current.has(batchId)) return;
		inFlightRef.current.add(batchId);

		setBatches((prev) => ({ ...prev, [batchId]: { batchId, actionIds, phase: "pending" } }));
		setAttemptByAction((prev) => {
			const next = { ...prev };
			actionIds.forEach((id) => {
				next[id] = batchId;
			});
			return next;
		});

		confirmMutation.mutate(
			{
				batch_id: batchId,
				items: ordered.map((action) => ({ action_type: action.action_type, params: action.params })),
				// panel_id omissible ONLY when the batch begins with create_wheel.
				...(ordered[0].action_type === "create_wheel" ? {} : { panel_id: target.panelId }),
				conversationUuid,
			},
			{
				onSuccess: (result) => {
					setBatches((prev) => ({
						...prev,
						[batchId]: { batchId, actionIds, phase: "confirmed", result, alreadyConfirmed: result.already_confirmed === true },
					}));
					target.onApplied({ panelId: result.panel_id ?? null, forkedFromPanelId: result.forked_from_panel_id ?? null });
				},
				onError: (error) => {
					setBatches((prev) => ({
						...prev,
						[batchId]: { batchId, actionIds, phase: "failed", error: confirmErrorFrom(error), failingItem: failingItemFrom(error) },
					}));
				},
				onSettled: () => {
					inFlightRef.current.delete(batchId);
				},
			},
		);
	};

	const undoBatch = (batchId: string) => {
		const record = batches[batchId] ?? rehydratedRecords[batchId];
		if (!record || record.phase !== "confirmed" || record.undoPending) return;
		const guardKey = `undo:${batchId}`;
		if (inFlightRef.current.has(guardKey)) return;
		inFlightRef.current.add(guardKey);

		setBatches((prev) => ({ ...prev, [batchId]: { ...record, ...prev[batchId], undoPending: true, undoBlocked: null, undoError: null } }));

		undoMutation.mutate(
			{ batchId },
			{
				onSuccess: (result) => {
					setBatches((prev) => ({
						...prev,
						[batchId]: { ...prev[batchId], phase: "undone", undoPending: false, undoResult: result, alreadyUndone: result.already_undone === true },
					}));
					target?.onApplied({ panelId: result.panel_id ?? null, forkedFromPanelId: null });
				},
				onError: (error) => {
					const status = axios.isAxiosError(error) ? error.response?.status : undefined;
					setBatches((prev) => ({
						...prev,
						[batchId]: {
							...prev[batchId],
							undoPending: false,
							...(status === 409 ? { undoBlocked: "Can't undo — wheel was published." } : { undoError: "Could not undo this change. Please try again." }),
						},
					}));
				},
				onSettled: () => {
					inFlightRef.current.delete(guardKey);
				},
			},
		);
	};

	const forAction = (action: XoeProposedAction): WheelActionCardState | undefined => {
		if (!isWheelActionType(action.action_type)) return undefined;

		const record = recordOf(action.id);
		const index = record ? record.actionIds.indexOf(action.id) : -1;
		const itemResult = record?.result?.items?.[index] ?? null;
		// Reversal entries arrive in REVERSE batch order (the server stores array_reverse(undoPlan)).
		const undoEntry = record?.undoResult?.items?.[record.actionIds.length - 1 - index] ?? null;
		const failing = record?.failingItem ?? null;

		return {
			canConfirm: target !== null,
			panelId: target?.panelId ?? null,
			phase: record?.phase ?? "idle",
			itemResult,
			alreadyConfirmed: record?.alreadyConfirmed === true,
			forkedFromPanelId: record?.result?.forked_from_panel_id ?? null,
			error: record?.error ?? null,
			failingItem: failing ? { ...failing, mine: failing.index === index } : null,
			undoEntry,
			alreadyUndone: record?.alreadyUndone === true,
			undoPending: record?.undoPending === true,
			undoBlocked: record?.undoBlocked ?? null,
			undoError: record?.undoError ?? null,
			onConfirm: () => confirmBatch([action]),
			onUndo: () => {
				if (record) undoBatch(record.batchId);
			},
		};
	};

	const openWheelActions = wheelActions.filter(isOpen);
	const anyPending = Object.values(batches).some((record) => record.phase === "pending");

	const addAll: WheelAddAllState | null =
		target && openWheelActions.length > 1
			? {
					count: openWheelActions.length,
					radius: describeWheelBlastRadius(openWheelActions),
					pending: anyPending,
					onConfirm: () => confirmBatch(openWheelActions),
				}
			: null;

	return {
		forAction,
		addAll,
	};
};
